'use client'

import { useEffect } from 'react'

// Portaled Radix popovers/menus/selects opened from inside the surface live outside it in the
// DOM; their lists must keep scrolling.
const POPPER = '[data-radix-popper-content-wrapper]'

/**
 * While `enabled`, the page behind a non-modal full-screen surface (the phone Order Form sheet)
 * can't scroll: the root stops scrolling, and a touch-drag that starts outside `allowSelector`
 * (and outside any popover opened from it) is cancelled. Scrollers inside the surface keep
 * working — give them `overscroll-contain` so reaching their end doesn't hand the gesture on.
 *
 * Not react-remove-scroll (what Radix modals use): that blocks everything outside one subtree,
 * which froze the lists of the sheet's own (portaled, non-modal) popovers.
 */
export function useLockPageScroll(enabled: boolean, allowSelector: string) {
  useEffect(() => {
    if (!enabled) return
    const { documentElement: html, body } = document
    const previous = { html: html.style.overflow, body: body.style.overflow }
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'

    const allowed = `${allowSelector}, ${POPPER}`
    const onTouchMove = (event: TouchEvent) => {
      const target = event.target
      if (target instanceof Element && target.closest(allowed)) return
      if (event.cancelable) event.preventDefault()
    }
    document.addEventListener('touchmove', onTouchMove, { passive: false })

    return () => {
      html.style.overflow = previous.html
      body.style.overflow = previous.body
      document.removeEventListener('touchmove', onTouchMove)
    }
  }, [enabled, allowSelector])
}
